import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import SkinAssistantNav from "../../components/admin/SkinAssistantNav";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import DashboardStatCard from "../../components/dashboard/DashboardStatCard";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import {
  API_NOT_DEPLOYED_MSG,
  CONCERN_OPTIONS,
  SKIN_TYPE_OPTIONS,
  getSkinAssistantResults,
  isSkinAssistantApiNotDeployed,
  labelForOption,
  parseResultsResponse,
} from "../../helper/skin_assistant_helper";
import { getAdminToken } from "../../helper/auth_helper";

function ResultsDashboard() {
  const token = getAdminToken();

  const [loading, setLoading] = useState(true);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [results, setResults] = useState([]);
  const [totals, setTotals] = useState({ total: 0 });
  const [countsByConcern, setCountsByConcern] = useState([]);
  const [countsBySkinType, setCountsBySkinType] = useState([]);
  const [pagination, setPagination] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [skinTypeFilter, setSkinTypeFilter] = useState("");
  const [concernFilter, setConcernFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchResults = useCallback(async () => {
    if (!token) {
      setLoadError("Not authorized");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    setApiUnavailable(false);
    try {
      const params = { page, limit: perPage };
      if (skinTypeFilter) params.skinType = skinTypeFilter;
      if (concernFilter) params.concern = concernFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await getSkinAssistantResults(params, token);
      const parsed = parseResultsResponse(res);
      if (parsed.error) {
        setLoadError(parsed.error);
        setResults([]);
        return;
      }
      setResults(parsed.results);
      setTotals(parsed.totals);
      setCountsByConcern(parsed.countsByConcern);
      setCountsBySkinType(parsed.countsBySkinType);
      setPagination(parsed.pagination);
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setApiUnavailable(true);
        setResults([]);
        setCountsByConcern([]);
        setCountsBySkinType([]);
      } else {
        setLoadError(error?.message || "Failed to load results");
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, skinTypeFilter, concernFilter, startDate, endDate]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const concernChartData = useMemo(
    () =>
      countsByConcern.map((row) => ({
        name: labelForOption(CONCERN_OPTIONS, row.label || row.key),
        count: row.count,
      })),
    [countsByConcern]
  );

  const skinTypeChartData = useMemo(
    () =>
      countsBySkinType.map((row) => ({
        name: labelForOption(SKIN_TYPE_OPTIONS, row.label || row.key),
        count: row.count,
      })),
    [countsBySkinType]
  );

  const columns = useMemo(
    () => [
      {
        name: "Date",
        selector: (row) => row.createdAt,
        sortable: true,
        width: "170px",
        cell: (row) =>
          row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A") : "—",
      },
      {
        name: "Skin type",
        selector: (row) => row.skinType,
        cell: (row) => labelForOption(SKIN_TYPE_OPTIONS, row.skinType),
      },
      {
        name: "Concern",
        selector: (row) => row.concern,
        cell: (row) => labelForOption(CONCERN_OPTIONS, row.concern),
      },
      {
        name: "Products",
        grow: 2,
        cell: (row) => {
          const ids = row.recommendedProductIds || [];
          if (!ids.length) return "—";
          return ids.slice(0, 3).join(", ") + (ids.length > 3 ? ` (+${ids.length - 3})` : "");
        },
      },
    ],
    []
  );

  const totalCount = pagination?.total ?? totals.total ?? results.length;

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Skin Assistant
              </li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Skin Assistant — Results</h4>
            <p className="text-secondary small mb-0 mt-1">
              Quiz completions, breakdowns, and recent recommendations
            </p>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={fetchResults} disabled={loading}>
            Refresh
          </Button>
        </div>

        <SkinAssistantNav />

        {apiUnavailable && (
          <Alert variant="warning" className="mb-3">{API_NOT_DEPLOYED_MSG}</Alert>
        )}
        {loadError && !apiUnavailable && (
          <Alert variant="danger" className="mb-3">{loadError}</Alert>
        )}

        <Row className="g-3 mb-4">
          <Col md={4}>
            <DashboardStatCard
              label="Total quiz results"
              value={apiUnavailable ? "—" : String(totalCount)}
              icon="ri-bar-chart-box-line"
            />
          </Col>
          <Col md={4}>
            <DashboardStatCard
              label="Top concern bucket"
              value={
                apiUnavailable || !concernChartData.length
                  ? "—"
                  : concernChartData.reduce((a, b) => (b.count > a.count ? b : a), concernChartData[0])?.name
              }
              icon="ri-heart-pulse-line"
            />
          </Col>
          <Col md={4}>
            <DashboardStatCard
              label="Top skin type"
              value={
                apiUnavailable || !skinTypeChartData.length
                  ? "—"
                  : skinTypeChartData.reduce((a, b) => (b.count > a.count ? b : a), skinTypeChartData[0])?.name
              }
              icon="ri-user-smile-line"
            />
          </Col>
        </Row>

        {!apiUnavailable && (concernChartData.length > 0 || skinTypeChartData.length > 0) && (
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <h6 className="mb-3">By concern</h6>
                  <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={concernChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#6f42c1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <h6 className="mb-3">By skin type</h6>
                  <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={skinTypeChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#20c997" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Card>
          <Card.Body>
            <Row className="g-2 mb-3 align-items-end">
              <Col md={3}>
                <Form.Label className="small">Skin type</Form.Label>
                <Form.Select
                  size="sm"
                  value={skinTypeFilter}
                  onChange={(e) => {
                    setPage(1);
                    setSkinTypeFilter(e.target.value);
                  }}
                >
                  <option value="">All</option>
                  {SKIN_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Concern</Form.Label>
                <Form.Select
                  size="sm"
                  value={concernFilter}
                  onChange={(e) => {
                    setPage(1);
                    setConcernFilter(e.target.value);
                  }}
                >
                  <option value="">All</option>
                  {CONCERN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small">From</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setPage(1);
                    setStartDate(e.target.value);
                  }}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small">To</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setPage(1);
                    setEndDate(e.target.value);
                  }}
                />
              </Col>
            </Row>

            {loading ? (
              <DataTableSkeleton rows={5} />
            ) : apiUnavailable ? (
              <AdminEmptyState
                icon="ri-cloud-off-line"
                title="Results API not available"
                description={API_NOT_DEPLOYED_MSG}
              />
            ) : results.length === 0 ? (
              <AdminEmptyState
                title="No results yet"
                description="Quiz results will appear here once customers complete the Skin Assistant on the storefront."
              />
            ) : (
              <DataTable
                columns={columns}
                data={results}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalCount}
                paginationPerPage={perPage}
                paginationDefaultPage={page}
                onChangePage={setPage}
                onChangeRowsPerPage={(newPerPage, newPage) => {
                  setPerPage(newPerPage);
                  setPage(newPage);
                }}
                paginationComponentOptions={dataTablePaginationOptions}
                highlightOnHover
                responsive
              />
            )}
          </Card.Body>
        </Card>
      </div>
      <Footer />
    </>
  );
}

export default ResultsDashboard;
